import React, { useEffect, useState } from 'react';
import { Clock, Smartphone } from 'lucide-react';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import { companyToday } from '../../utils/dateUtils';
import useCardData from './useCardData';
import { DashboardCard, CardLink, CardError } from './DashboardCard';
import {
  formatClock, formatLongDay, formatMinutes, greetingFor,
} from './format';

const TONES = {
  success: { dot: 'bg-success-500', badge: 'bg-success-50 text-success-700' },
  warning: { dot: 'bg-warning-500', badge: 'bg-warning-50 text-warning-700' },
  danger: { dot: 'bg-danger-500', badge: 'bg-danger-50 text-danger-700' },
  info: { dot: 'bg-primary-500', badge: 'bg-primary-50 text-primary-700' },
  muted: { dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-700' },
};

/**
 * Today's status in words, from GET /api/timesheet/today. That endpoint already resolves
 * the company day, leave and holidays (timesheetDay.js), so this only names the result.
 */
export function attendanceStatus(day) {
  const signedIn = Boolean(day.in_at);
  if (!signedIn && day.day_type === 'holiday') {
    return { tone: 'info', label: day.holiday_name ? `Holiday: ${day.holiday_name}` : 'Public holiday' };
  }
  if (!signedIn && day.day_type === 'leave') return { tone: 'info', label: 'On leave today' };
  if (!signedIn && day.day_type === 'weekend') return { tone: 'muted', label: 'Weekend' };
  switch (day.state) {
    case 'timed_in': return { tone: 'success', label: 'Signed in' };
    case 'timed_out': return { tone: 'muted', label: 'Signed out' };
    case 'unclosed': return { tone: 'warning', label: 'Sign-out missing' };
    case 'absent': return { tone: 'danger', label: 'Absent' };
    default: return { tone: 'muted', label: 'Not signed in yet' };
  }
}

const TimeRow = ({ label, value, dot, testId }) => (
  <div className="flex items-center justify-between py-2 text-sm">
    <span className="flex items-center gap-2 text-gray-600">
      <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
      {label}
    </span>
    <span data-testid={testId} className="font-medium text-gray-900 tabular-nums">{value}</span>
  </div>
);

const TodayAttendance = ({ day }) => {
  const [now, setNow] = useState(() => Date.now());
  const open = day.state === 'timed_in' && day.open_since;

  // worked_minutes counts closed stretches only; a running one is added live.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, [open]);

  const running = open ? Math.max(0, (now - new Date(day.open_since).getTime()) / 60000) : 0;
  const worked = (day.worked_minutes || 0) + running;
  const expected = day.expected_minutes || 0;
  const progress = expected > 0 ? Math.min(100, Math.round((worked / expected) * 100)) : 0;
  const status = attendanceStatus(day);
  const tone = TONES[status.tone];
  const zone = day.timezone || undefined;

  return (
    <div data-testid="dashboard-welcome-attendance">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Today</span>
        <span
          data-testid="dashboard-welcome-status"
          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${tone.badge}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
          {status.label}
        </span>
      </div>
      <div className="mt-2 divide-y divide-gray-100">
        <TimeRow
          label="Signed in"
          dot={day.in_at ? 'bg-success-500' : 'bg-gray-300'}
          value={day.in_at ? formatClock(day.in_at, zone) : 'Not yet'}
          testId="dashboard-welcome-signed-in"
        />
        <TimeRow
          label="Signed out"
          dot={day.out_at && !open ? 'bg-danger-500' : 'bg-gray-300'}
          value={day.out_at && !open ? formatClock(day.out_at, zone) : '-'}
          testId="dashboard-welcome-signed-out"
        />
      </div>
      <div className="mt-3">
        <div className="flex items-baseline gap-2">
          <span data-testid="dashboard-welcome-hours" className="text-3xl font-bold text-gray-900 tabular-nums">
            {formatMinutes(worked)}
          </span>
          <span className="text-sm text-gray-500">
            {expected > 0 ? `of ${formatMinutes(expected)} today` : 'today'}
          </span>
        </div>
        {expected > 0 && (
          <div
            className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden"
            role="progressbar"
            aria-label="Today's hours against your daily target"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Greeting plus today's attendance. Sign-in and sign-out happen in the ByteCorp Time
 * mobile app (punches need an approved device), so there is deliberately no clock-in
 * button here.
 */
const WelcomeCard = ({ className = '' }) => {
  const { user, hasAnyResourceAction, hasPermission } = useAuth();
  // GET /api/timesheet/today is self-only and needs an employee record; asking without
  // one answers ONBOARDING_REQUIRED, which the axios interceptor turns into a redirect.
  const canSeeToday = Boolean(user?.employee_id)
    && hasAnyResourceAction('timesheet', ['read_own', 'read_department', 'read_all']);
  const canSeeMyAttendance = Boolean(user?.employee_id) && hasPermission('attendance', 'read_own');
  // Their own documents folder lives on their Profile page (ALEE-2).
  const canSeeMyDocuments = Boolean(user?.employee_id) && hasAnyResourceAction('documents', ['create', 'create_own']);

  const today = useCardData(
    async () => (canSeeToday ? (await api.get('/api/timesheet/today')).data.data : null),
    [canSeeToday]
  );

  const name = user?.first_name || user?.username || '';

  return (
    <DashboardCard
      data-testid="dashboard-welcome"
      title={`${greetingFor()}${name ? `, ${name}` : ''}`}
      subtitle={formatLongDay(companyToday())}
      icon={Clock}
      className={className}
    >
      {canSeeToday ? (
        <>
          {today.loading && (
            <div role="status" aria-label="Loading today's attendance" className="space-y-3 animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-1/2" />
              <div className="h-4 bg-gray-100 rounded" />
              <div className="h-4 bg-gray-100 rounded" />
              <div className="h-8 bg-gray-100 rounded w-1/3" />
            </div>
          )}
          {today.error && (
            <CardError
              data-testid="dashboard-welcome-error"
              message="Could not load today's attendance."
              onRetry={today.reload}
            />
          )}
          {today.data && <TodayAttendance day={today.data} />}
          <div className="mt-auto pt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <p className="flex items-center gap-1.5 text-xs text-gray-500">
              <Smartphone className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              Sign in and out from the ByteCorp Time app.
            </p>
            {(canSeeMyAttendance || canSeeMyDocuments) && (
              <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {canSeeMyDocuments && (
                  <CardLink to="/profile#documents" data-testid="dashboard-my-documents">My documents</CardLink>
                )}
                {canSeeMyAttendance && <CardLink to="/my-attendance">My attendance</CardLink>}
              </span>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-600">
          Your attendance and time off appear here once your account is linked to an employee record.
        </p>
      )}
    </DashboardCard>
  );
};

export default WelcomeCard;
